import { type ComponentProps, type MutableRefObject, useRef } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { Link as RouterLink } from '@change-corgi/core/react/router';
import { FakeLink } from '@change-corgi/design-system/components/actions';
import { Box } from '@change-corgi/design-system/layout';
import { linkResetStyles } from '@change-corgi/design-system/reset';
import { Text } from '@change-corgi/design-system/typography';

import type { Comment } from 'src/app/pages/petition/comments/pageContext';
import { VerticalEllipsis } from 'src/app/shared/components/layout/ellipsis';
import { useFocusFallbackRef } from 'src/app/shared/hooks/focus';

import { CommentFooter } from './components/CommentFooter';
import { CommentHeader } from './components/CommentHeader';
import { useCommentCard } from './hooks/useCommentCard';

type Props = {
	comment: Comment;
	commentLinkRef?: MutableRefObject<null>;
};

export function CommentCard({ comment, commentLinkRef, ...rest }: Props & ComponentProps<typeof Box>) {
	const {
		data: { comment: commentText, commentUrl, expanded },
		actions: { expandComment },
	} = useCommentCard(comment);

	const localCommentLinkRef = useRef(null);

	// since clicking on "Read more" removes it, we need to move the focus to the comment card
	const containerRef = useFocusFallbackRef(({ focus }) => {
		if (localCommentLinkRef.current) return focus(localCommentLinkRef.current, { preventScroll: true });
		if (commentLinkRef?.current) return focus(commentLinkRef.current, { preventScroll: true });
		return false;
	});

	return (
		<Box variant="bordered" p={16} {...rest} ref={containerRef} data-qa="comment-card" data-expanded={expanded}>
			<CommentHeader comment={comment} />
			<RouterLink
				to={commentUrl}
				sx={{ ...linkResetStyles, display: 'block', my: 8 }}
				ref={commentLinkRef || localCommentLinkRef}
			>
				{expanded ? (
					<Text as="div" size="default" breakWord sx={{ whiteSpace: 'pre-wrap' }} data-qa="comment-body">
						{commentText}
					</Text>
				) : (
					<VerticalEllipsis
						height={72}
						watchResize
						ellipsis={
							<>
								…{' '}
								<FakeLink
									onClick={(e) => {
										e.preventDefault();
										expandComment();
									}}
									data-qa="comment-read-more"
								>
									<Translate value="fe.pages.common.read_more" />
								</FakeLink>
							</>
						}
					>
						<Text as="div" size="default" breakWord sx={{ whiteSpace: 'pre-wrap' }} data-qa="comment-body">
							{commentText}
						</Text>
					</VerticalEllipsis>
				)}
			</RouterLink>
			<CommentFooter comment={comment} />
		</Box>
	);
}
